import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Check, X, Settings2 } from "lucide-react";
import { apiClient } from "@/App";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const inputCls =
  "w-full rounded-md bg-[#0b1928] border border-[#243a52] focus:border-[#0ea5e9]/70 focus:outline-none px-3 py-2 text-sm text-white placeholder:text-slate-600";
const fieldLabelCls = "text-[10px] uppercase tracking-[0.25em] text-slate-400 mb-1";

function CodeLine({ label, value, testid }) {
  if (!value) return null;
  return (
    <div className="mt-1.5" data-testid={testid}>
      <div className="text-[9px] uppercase tracking-[0.2em] text-slate-500">{label}</div>
      <pre className="text-xs text-slate-300 whitespace-pre-wrap font-sans leading-relaxed">{value}</pre>
    </div>
  );
}

function CodeForm({ initial, onCancel, onSaved, testPrefix }) {
  const [code, setCode] = useState(initial?.code || "");
  const [kejadian, setKejadian] = useState(initial?.kejadian || "");
  const [text, setText] = useState(initial?.text || "");
  const [penyebab, setPenyebab] = useState(initial?.penyebab || "");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!code.trim()) {
      toast.error("Kode wajib diisi");
      return;
    }
    if (!kejadian.trim() && !text.trim()) {
      toast.error("Isi minimal Kejadian dan Dampak atau Penanganan Gangguan");
      return;
    }
    setBusy(true);
    try {
      const body = { code: code.trim(), kejadian: kejadian.trim(), text: text.trim(), penyebab: penyebab.trim() };
      const { data } = initial?.id
        ? await apiClient.put(`/reports/handling-codes/${initial.id}`, body)
        : await apiClient.post("/reports/handling-codes", body);
      onSaved(data);
      toast.success(initial?.id ? "Kode diperbarui" : "Kode ditambahkan");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan kode");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-[#0ea5e9]/40 bg-[#0ea5e9]/5 p-3 space-y-2">
      <input
        data-testid={`${testPrefix}-code-input`}
        value={code}
        onChange={(e) => setCode(e.target.value.toUpperCase())}
        placeholder="KODE (contoh: WRS_ATO)"
        className={`${inputCls} font-mono uppercase`}
      />
      <div>
        <div className={fieldLabelCls}>Kejadian dan Dampak</div>
        <textarea
          data-testid={`${testPrefix}-kejadian-input`}
          value={kejadian}
          onChange={(e) => setKejadian(e.target.value)}
          rows={2}
          placeholder="contoh: TS15 EB di petak jalan SNY-SSM, delay tiba ASN 01:18"
          className={`${inputCls} resize-y leading-relaxed`}
        />
      </div>
      <div>
        <div className={fieldLabelCls}>Penanganan Gangguan</div>
        <textarea
          data-testid={`${testPrefix}-text-input`}
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={4}
          placeholder={"Satu langkah per baris:\n- Masinis release EB\n- OCC memberikan ATO Depart"}
          className={`${inputCls} resize-y leading-relaxed`}
        />
      </div>
      <div>
        <div className={fieldLabelCls}>Penyebab Gangguan <span className="normal-case tracking-normal text-slate-600">(opsional)</span></div>
        <textarea
          data-testid={`${testPrefix}-penyebab-input`}
          value={penyebab}
          onChange={(e) => setPenyebab(e.target.value)}
          rows={2}
          placeholder="Dugaan root cause"
          className={`${inputCls} resize-y leading-relaxed`}
        />
      </div>
      <div className="flex justify-end gap-2">
        <button
          type="button"
          data-testid={`${testPrefix}-cancel-btn`}
          onClick={onCancel}
          className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-md border border-[#243a52] text-slate-300 hover:text-white"
        >
          <X className="h-3.5 w-3.5" /> Batal
        </button>
        <button
          type="button"
          data-testid={`${testPrefix}-save-btn`}
          onClick={submit}
          disabled={busy}
          className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-md bg-[#0ea5e9] text-[#0c1b2a] font-semibold hover:bg-[#38bdf8] disabled:opacity-50"
        >
          <Check className="h-3.5 w-3.5" /> Simpan
        </button>
      </div>
    </div>
  );
}

export default function HandlingCodesDialog({ open, onOpenChange, codes, onCodesChange }) {
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    if (!open) {
      setAdding(false);
      setEditingId(null);
    }
  }, [open]);

  const remove = async (c) => {
    if (!window.confirm(`Hapus kode ${c.code}?`)) return;
    try {
      await apiClient.delete(`/reports/handling-codes/${c.id}`);
      onCodesChange(codes.filter((x) => x.id !== c.id));
      toast.success(`Kode ${c.code} dihapus`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menghapus kode");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="handling-codes-dialog"
        className="max-w-2xl bg-[#0d243a] border-[#243a52] text-white max-h-[85vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Settings2 className="h-5 w-5 text-[#7dd3fc]" />
            Kelola Kode Penanganan
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Satu kode memetakan ke Kejadian dan Dampak, Penanganan Gangguan, dan Penyebab
            (opsional). Ketik kode di salah satu field tersebut, ketiga field akan terisi otomatis.
            Data berlaku untuk semua user.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {!adding && (
            <button
              type="button"
              data-testid="add-code-btn"
              onClick={() => {
                setAdding(true);
                setEditingId(null);
              }}
              className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-md bg-[#289643]/20 border border-[#22c55e]/40 text-[#22c55e] hover:bg-[#289643]/30"
            >
              <Plus className="h-4 w-4" /> Tambah Kode
            </button>
          )}
          {adding && (
            <CodeForm
              testPrefix="new-code"
              onCancel={() => setAdding(false)}
              onSaved={(d) => {
                onCodesChange([...codes, d].sort((a, b) => a.code.localeCompare(b.code)));
                setAdding(false);
              }}
            />
          )}

          {codes.length === 0 && (
            <div className="text-sm text-slate-500 italic" data-testid="codes-empty">
              Belum ada kode penanganan.
            </div>
          )}

          <ul className="space-y-2" data-testid="codes-list">
            {codes.map((c) =>
              editingId === c.id ? (
                <li key={c.id}>
                  <CodeForm
                    testPrefix={`edit-code-${c.code}`}
                    initial={c}
                    onCancel={() => setEditingId(null)}
                    onSaved={(d) => {
                      onCodesChange(
                        codes
                          .map((x) => (x.id === d.id ? d : x))
                          .sort((a, b) => a.code.localeCompare(b.code)),
                      );
                      setEditingId(null);
                    }}
                  />
                </li>
              ) : (
                <li
                  key={c.id}
                  data-testid={`code-item-${c.code}`}
                  className="rounded-lg border border-[#243a52] bg-[#0b1928] p-3 flex gap-3"
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-sm text-[#7dd3fc] font-semibold">{c.code}</div>
                    <CodeLine label="Kejadian dan Dampak" value={c.kejadian} testid={`code-kejadian-${c.code}`} />
                    <CodeLine label="Penanganan Gangguan" value={c.text} testid={`code-text-${c.code}`} />
                    <CodeLine label="Penyebab Gangguan" value={c.penyebab} testid={`code-penyebab-${c.code}`} />
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    <button
                      type="button"
                      data-testid={`edit-code-btn-${c.code}`}
                      onClick={() => {
                        setEditingId(c.id);
                        setAdding(false);
                      }}
                      className="p-1.5 rounded-md border border-[#243a52] text-slate-300 hover:text-white hover:border-[#0ea5e9]/60"
                      title="Edit"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      data-testid={`delete-code-btn-${c.code}`}
                      onClick={() => remove(c)}
                      className="p-1.5 rounded-md border border-[#243a52] text-slate-300 hover:text-red-300 hover:border-red-500/60"
                      title="Hapus"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </li>
              ),
            )}
          </ul>
        </div>
      </DialogContent>
    </Dialog>
  );
}
